import { addAuthorizationHeader } from '@/lib/functions/addAuthorizationHeader';
import {
  readCheckoutFormDraft,
  writeCheckoutFormDraft,
} from '@/lib/functions/checkoutFormDraft';

import type { OrderPayload } from '@/lib/types/orderPayload';

const NO_SUBSTITUTIONS_KEY = (token: string) => `partyNoSubs:${token}`;

export function readPartyOrderNoSubstitutions(token: string | null | undefined): boolean {
  if (!token || typeof window === 'undefined') {
    return false;
  }

  if (sessionStorage.getItem(NO_SUBSTITUTIONS_KEY(token)) === '1') {
    return true;
  }

  const draft = readCheckoutFormDraft(token);
  return draft?.noSubstitutions === true;
}

export function markPartyOrderNoSubstitutions(token: string, optedOut: boolean): void {
  if (typeof window === 'undefined' || !token) {
    return;
  }

  if (optedOut) {
    sessionStorage.setItem(NO_SUBSTITUTIONS_KEY(token), '1');
  } else {
    sessionStorage.removeItem(NO_SUBSTITUTIONS_KEY(token));
  }
}

export function syncPartyOrderNoSubstitutionsFromOrder(
  token: string,
  allowSubstitutions: boolean,
): void {
  if (typeof window === 'undefined' || !token) {
    return;
  }

  markPartyOrderNoSubstitutions(token, !allowSubstitutions);

  const draft = readCheckoutFormDraft(token);
  if (!draft) {
    return;
  }

  const noSubstitutions = !allowSubstitutions;
  if (draft.noSubstitutions === noSubstitutions) {
    return;
  }

  writeCheckoutFormDraft(token, { ...draft, noSubstitutions });
}

/**
 * Resolves allowSubstitutions for cart PUTs. Session/draft opt-out wins over stale order payload.
 */
export function resolvePartyAllowSubstitutionsForCartUpdate(
  token: string | null | undefined,
  orderAllowSubstitutions?: boolean,
): boolean {
  if (readPartyOrderNoSubstitutions(token)) {
    return false;
  }

  return orderAllowSubstitutions ?? true;
}

async function applyPartyOrderSubstitutions(
  token: string,
  allowSubstitutions: boolean,
): Promise<boolean> {
  const headers = addAuthorizationHeader(
    { 'Content-Type': 'application/json' },
    null,
  );

  const response = await fetch(`/api/orders/${token}/details`, {
    method: 'POST',
    cache: 'no-cache',
    headers,
    body: JSON.stringify({ allowSubstitutions }),
  });

  return response.ok;
}

/**
 * Party Supplies defaults to substitutions enabled with a 15% authorization buffer.
 * Applies that setting on the Sylius order when it has not been applied yet.
 *
 * Cart address updates (PUT) omitting allowSubstitutions no longer change the order setting;
 * the storefront always sends the resolved value explicitly.
 *
 * @returns true when substitutions were changed and the cart should be re-fetched.
 */
export async function ensurePartyOrderSubstitutions(
  token: string,
  fetched: OrderPayload | null,
): Promise<boolean> {
  if (!fetched || !token) {
    return false;
  }

  const allowSubs = fetched.settings?.allowSubstitutions ?? true;
  syncPartyOrderNoSubstitutionsFromOrder(token, allowSubs);

  if (!allowSubs) {
    const bufferAmount = Number(fetched.pricing?.buffer ?? 0);
    if (bufferAmount === 0) {
      // Reprocess so shipping TaT is applied while substitutions stay off.
      return applyPartyOrderSubstitutions(token, false);
    }

    return false;
  }

  const bufferAmount = Number(fetched.pricing?.buffer ?? 0);
  if (bufferAmount > 0) {
    return false;
  }

  return applyPartyOrderSubstitutions(token, true);
}
